import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * Public test flow via /test/:token
 * - Loads player + questions
 * - Renders one question per screen with 4 options
 * - Progress bar
 * - Submits at end -> redirects to /test/:token/result
 */
export default function PublicTest() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [step, setStep] = useState(-1); // -1 = welcome, 0..N-1 = question
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/test/${token}`);
        setState({ loading: false, ...data });
      } catch (e) {
        setState({ loading: false, error: formatApiError(e) });
      }
    })();
  }, [token]);

  if (state.loading)
    return <FullscreenMsg text="Test wird geladen …" />;
  if (state.error)
    return <FullscreenMsg text={state.error} error />;
  if (state.already_completed)
    return (
      <FullscreenMsg
        text="Dieser Test wurde bereits abgeschlossen. Frage deinen Trainer nach dem Ergebnis."
        error
      />
    );

  const questions = state.questions;
  const total = questions.length;
  const progress = step < 0 ? 0 : Math.round(((step + 1) / total) * 100);

  const selectAnswer = (qid, optId) => {
    setAnswers((a) => ({ ...a, [qid]: optId }));
    // auto-advance after short delay
    setTimeout(() => {
      if (step < total - 1) setStep(step + 1);
    }, 250);
  };

  const submit = async () => {
    if (Object.keys(answers).length < total) {
      toast.error(`Bitte alle ${total} Fragen beantworten.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/test/${token}/submit`, { answers });
      // Store analysis in sessionStorage for result screen
      sessionStorage.setItem(
        "fmp_test_result",
        JSON.stringify({ analysis: data.analysis, player: state.player, team: state.team })
      );
      nav(`/test/${token}/result`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Welcome screen ---
  if (step === -1) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / FOOTBALL MOTIVATION PROFILE
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl leading-none tracking-tight mb-6">
            Hallo <span className="text-[#CCFF00]">{state.player.name}</span>.
          </h1>
          <p className="text-lg text-white/70 leading-relaxed mb-6">
            {state.team.name}
            {state.team.age_group ? ` · ${state.team.age_group}` : ""}
          </p>
          <div className="space-y-4 mb-10 text-white/80">
            <p>
              Im nächsten Schritt beantwortest du <b>60 realistische Situationen</b>,
              die du aus dem Fußballalltag kennst. Es gibt <b>keine richtigen oder falschen Antworten</b> –
              es geht darum, wie du wirklich reagierst.
            </p>
            <ul className="space-y-2 text-sm text-white/60">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                Antworte spontan – dein erster Gedanke ist meistens der ehrlichste.
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                Dauer: ca. 10–12 Minuten. Am besten ohne Ablenkung.
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                Dein Trainer sieht dein Ergebnis und die Team-Auswertung.
              </li>
            </ul>
          </div>
          <button
            data-testid="test-start-btn"
            onClick={() => setStep(0)}
            className="btn-primary"
          >
            Test starten
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // --- Question screen ---
  const q = questions[step];
  const isLast = step === total - 1;
  const answered = answers[q.id] !== undefined;

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Progress */}
      <div className="sticky top-0 z-10 bg-[#050505]/95 backdrop-blur-md border-b border-white/5">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="font-display tracking-widest text-white/60">
              Frage {step + 1} / {total}
            </span>
            <span className="font-display tracking-widest text-[#CCFF00]">
              {progress}%
            </span>
          </div>
          <div className="progress-bar">
            <div className="fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center px-6 py-12">
        <div className="mx-auto max-w-3xl w-full">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-4">
            / SITUATION {String(step + 1).padStart(2, "0")}
          </div>
          <h2
            data-testid={`question-text-${step}`}
            className="font-heading font-bold text-2xl md:text-3xl leading-snug mb-8"
          >
            {q.text}
          </h2>
          <div className="space-y-3 mb-8">
            {q.options.map((opt) => (
              <button
                key={opt.id}
                data-testid={`option-${step}-${opt.id}`}
                onClick={() => selectAnswer(q.id, opt.id)}
                className={`option-card ${
                  answers[q.id] === opt.id ? "selected" : ""
                }`}
              >
                <span className="bullet" />
                <span className="text-base leading-snug">{opt.text}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <button
              onClick={() => step > 0 && setStep(step - 1)}
              disabled={step === 0}
              data-testid="test-prev-btn"
              className="btn-ghost !py-2 !px-4 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
            {isLast ? (
              <button
                data-testid="test-submit-btn"
                onClick={submit}
                disabled={!answered || submitting}
                className="btn-primary disabled:opacity-40"
              >
                {submitting ? "Wird ausgewertet …" : "Test abschließen"}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                data-testid="test-next-btn"
                onClick={() => setStep(step + 1)}
                disabled={!answered}
                className="btn-primary disabled:opacity-40"
              >
                Weiter
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FullscreenMsg({ text, error }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center">
      <div>
        <div
          className={`font-display text-xs tracking-[0.3em] mb-3 ${
            error ? "text-red-400" : "text-[#CCFF00]"
          }`}
        >
          / FMP
        </div>
        <div className="font-heading text-2xl">{text}</div>
      </div>
    </div>
  );
}
